import { useState, useEffect } from 'react';
import { fetchAPI, postAPI } from '../../utils/utils';
import UserInfo from '../login/UserInfo';
import HeaderTabs from '../interface/HeaderTabs';
import Pressable from '../other/Pressable';

const DEFAULT_FOLDER_ICON = '📁';

function TrashView({ userInfo, castellersInfo, onClose, refreshData, hideHeader, ...props }) {
    const [trashLinks, setTrashLinks] = useState([]);
    const [trashFolders, setTrashFolders] = useState([]);
    const [currentFolder, setCurrentFolder] = useState(null);

    const styles = {
        container: {
            padding: '30px',
            maxWidth: '90%',
            boxSizing: 'border-box',
            margin: '0 auto',
        },
        header: {
            marginTop: '30px',
            color: '#333'
        },
        linkCard: {
            backgroundColor: '#fff',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            marginBottom: '20px',
            transition: 'transform 0.2s'
        },
        linkTitle: {
            fontSize: '20px',
            color: '#007bff',
            textDecoration: 'none',
            marginBottom: '10px'
        },
        linkUrl: {
            fontSize: '14px',
            color: '#888',
            marginBottom: '10px'
        },
        linkDescription: {
            fontSize: '16px',
            color: '#555',
            marginBottom: '15px'
        },
        folderCard: {
            backgroundColor: '#e8f4f8',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            marginBottom: '15px',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            border: '1px solid #bee5eb'
        },
        folderIcon: {
            fontSize: '24px'
        },
        folderName: {
            fontSize: '18px',
            fontWeight: '500',
            color: '#0c5460',
            flex: 1
        },
        folderArrow: {
            fontSize: '20px',
            color: '#0c5460',
            fontWeight: 'bold'
        },
        navigationHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginBottom: '20px'
        },
        backButton: {
            padding: '8px 16px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            color: '#007bff',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            textDecoration: 'none'
        }
    };

    useEffect(() => {
        fetchAPI('/trash_links', setTrashLinks);
        fetchAPI('/trash_folders', setTrashFolders);
    }, []);

    // Socket event listeners for real-time trash updates
    useEffect(() => {
        if (!props.socket) return;

        const socket = props.socket;

        // Link restored event - remove from trash
        const onLinkRestored = (data) => {
            const { id } = data;
            setTrashLinks(prevLinks => prevLinks.filter(link => link.id !== id));
            // Also refresh from API to ensure cache is updated
            fetchAPI('/trash_links', setTrashLinks, false);
        };
        socket.on('.link_restored', onLinkRestored);

        // Folder restored event - remove folder and its contents from trash
        const onFolderRestored = (data) => {
            const { id, restoredSubfolders, restoredLinks } = data;
            
            // Remove main folder
            setTrashFolders(prevFolders => prevFolders.filter(folder => folder.id !== id));
            
            // Remove restored subfolders
            if (restoredSubfolders && restoredSubfolders.length > 0) {
                const restoredSubfolderIds = restoredSubfolders.map(f => f.id);
                setTrashFolders(prevFolders => 
                    prevFolders.filter(folder => !restoredSubfolderIds.includes(folder.id))
                );
            }
            
            // Remove restored links
            if (restoredLinks && restoredLinks.length > 0) {
                const restoredLinkIds = restoredLinks.map(l => l.id);
                setTrashLinks(prevLinks => 
                    prevLinks.filter(link => !restoredLinkIds.includes(link.id))
                );
            }
            
            // Also refresh from API to ensure cache is updated
            fetchAPI('/trash_folders', setTrashFolders, false);
            fetchAPI('/trash_links', setTrashLinks, false);
        };
        socket.on('.folder_restored', onFolderRestored);

        return () => {
            socket.off('.link_restored', onLinkRestored);
            socket.off('.folder_restored', onFolderRestored);
        };
    }, [props.socket]);

    const goBackFolder = () => {
        if (currentFolder) {
            const folder = trashFolders.find(f => f.id === currentFolder);
            setCurrentFolder(folder ? folder.parentId || null : null);
        }
    };

    const handleRestoreLink = (id) => {
        postAPI('/restore_link', { id }, () => {
            // Socket event will handle local state update
            refreshData();
        });
    };

    const handleRestoreFolder = (id) => {
        postAPI('/restore_folder', { id }, () => {
            // Socket event will handle local state update
            refreshData();
        });
    };

    const handlePermanentDeleteLink = (id) => {
        if (window.confirm('Estàs segur que vols eliminar permanentment aquest enllaç? Aquesta acció no es pot desfer.')) {
            postAPI('/permanent_delete_link', { id }, () => {
                // Update local state immediately
                setTrashLinks(prevLinks => prevLinks.filter(link => link.id !== id));
            });
        }
    };

    const handlePermanentDeleteFolder = (id) => {
        if (window.confirm('Estàs segur que vols eliminar permanentment aquesta carpeta? Aquesta acció no es pot desfer.')) {
            postAPI('/permanent_delete_folder', { id }, () => {
                // Update local state immediately
                setTrashFolders(prevFolders => prevFolders.filter(folder => folder.id !== id));
            });
        }
    };

    const handleEmptyTrash = () => {
        if (window.confirm('Estàs segur que vols buidar completament la paperera? Tots els elements s\'eliminaran permanentment i aquesta acció no es pot desfer.')) {
            postAPI('/empty_trash', {}, () => {
                // Update local state immediately
                setTrashLinks([]);
                setTrashFolders([]);
            });
        }
    };

    const getCurrentLinks = () => {
        return currentFolder
            ? trashLinks.filter(l => l.folderId === currentFolder)
            : trashLinks.filter(l => !l.folderId);
    };

    const getCurrentFolders = () => {
        // Show all folders at root level of trash, but maintain hierarchy when navigating
        if (!currentFolder) {
            // At root level, show only top-level folders that were deleted
            // (folders whose parents are not also in trash, or show all for simplicity)
            return trashFolders.filter(folder => {
                // Check if this folder's parent is also in trash
                const parentInTrash = trashFolders.some(f => f.id === folder.parentId);
                // If parent is not in trash, show at root level
                return !parentInTrash;
            });
        } else {
            // When navigating within trash, show subfolders
            return trashFolders.filter(f => f.parentId === currentFolder);
        }
    };

    const getCurrentFolderName = () => {
        if (!currentFolder) return null;
        const folder = trashFolders.find(f => f.id === currentFolder);
        return folder ? folder.name : null;
    };

    const getCombinedItems = () => {
        const currentFolders = getCurrentFolders().map(folder => ({ ...folder, type: 'folder' }));
        const currentLinks = getCurrentLinks().map(link => ({ ...link, type: 'link' }));
        return [...currentFolders, ...currentLinks].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    };

    const renderTrashLinkCard = (link) => {
        return (
            <div key={`trash-link-${link.id}`} style={{
                ...styles.linkCard,
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                opacity: 0.8
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                }}>
                    <div style={{flex: 1}}>
                        <div style={{
                            ...styles.linkTitle,
                            color: '#6c757d',
                            textDecoration: 'none',
                            display: 'block',
                            fontWeight: 500,
                            marginBottom: '4px'
                        }}>
                            {link.title}
                        </div>
                        <p style={{
                            ...styles.linkUrl,
                            margin: 0,
                            fontSize: '13px',
                            color: '#adb5bd'
                        }}>
                            {link.url}
                        </p>
                        <p style={{
                            fontSize: '12px',
                            color: '#6c757d',
                            margin: '4px 0 0 0'
                        }}>
                            Eliminat: {new Date(link.deletedAt).toLocaleDateString('ca-ES')}
                        </p>
                    </div>
                    <div style={{
                        display: 'flex',
                        gap: '8px'
                    }}>
                        <button
                            onClick={() => handleRestoreLink(link.id)}
                            style={{
                                padding: '6px 12px',
                                border: '1px solid #28a745',
                                borderRadius: '4px',
                                background: '#fff',
                                color: '#28a745',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            Restaurar
                        </button>
                        <button
                            onClick={() => handlePermanentDeleteLink(link.id)}
                            style={{
                                padding: '6px 12px',
                                border: '1px solid #dc3545',
                                borderRadius: '4px',
                                background: '#fff',
                                color: '#dc3545',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            Eliminar permanentment
                        </button>
                    </div>
                </div>
                {link.description && (
                    <p style={{
                        ...styles.linkDescription,
                        margin: '8px 0 0 0',
                        fontSize: '15px',
                        lineHeight: '1.5',
                        color: '#6c757d'
                    }}>
                        {link.description}
                    </p>
                )}
            </div>
        );
    };

    const renderTrashFolderCard = (folder) => {
        return (
            <div key={`trash-folder-${folder.id}`} style={{
                ...styles.folderCard,
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                opacity: 0.8
            }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        cursor: 'pointer'
                    }}
                    onClick={() => setCurrentFolder(folder.id)}
                >
                    <span style={styles.folderIcon}>{folder.icon || DEFAULT_FOLDER_ICON}</span>
                    <div style={{ flex: 1 }}>
                        <div style={{
                            ...styles.folderName,
                            color: '#6c757d'
                        }}>
                            {folder.name}
                        </div>
                        {folder.description && (
                            <div style={{
                                fontSize: '14px',
                                color: '#adb5bd',
                                marginTop: '2px',
                                lineHeight: '1.3'
                            }}>
                                {folder.description}
                            </div>
                        )}
                        <div style={{
                            fontSize: '12px',
                            color: '#6c757d',
                            marginTop: '4px'
                        }}>
                            Eliminat: {new Date(folder.deletedAt).toLocaleDateString('ca-ES')}
                        </div>
                    </div>
                    <span style={styles.folderArrow}>›</span>
                </div>
                <div style={{
                    display: 'flex',
                    gap: '8px'
                }}>
                    <button
                        onClick={() => handleRestoreFolder(folder.id)}
                        style={{
                            padding: '6px 12px',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            background: '#fff',
                            color: '#28a745',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        Restaurar carpeta
                    </button>
                    <button
                        onClick={() => handlePermanentDeleteFolder(folder.id)}
                        style={{
                            padding: '6px 12px',
                            border: '1px solid #dc3545',
                            borderRadius: '4px',
                            background: '#fff',
                            color: '#dc3545',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        Eliminar permanentment
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div style={hideHeader ? {} : styles.container}>
            {!hideHeader && (
                <>
                    <UserInfo userInfo={userInfo} castellersInfo={castellersInfo} {...props} />
                    <HeaderTabs userInfo={userInfo} {...props} />
                    <Pressable style={{ backgroundColor: '#eee' }} className="boto-back" href='/'>
                        ← Tornar a la pàgina principal
                    </Pressable>
                </>
            )}

            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px'
            }}>
                <div style={currentFolder ? styles.navigationHeader : { display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {currentFolder && (
                        <button
                            onClick={goBackFolder}
                            style={styles.backButton}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        >
                            <span>←</span>
                            Tornar
                        </button>
                    )}
                    <h2 style={{ ...styles.header, margin: 0 }}>
                        {currentFolder ? getCurrentFolderName() : '🗑️ Paperera'}
                    </h2>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={() => { onClose(); }}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#6c757d',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        ← Tornar als enllaços
                    </button>
                    <button
                        onClick={handleEmptyTrash}
                        disabled={trashLinks.length === 0 && trashFolders.length === 0}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#dc3545',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (trashLinks.length > 0 || trashFolders.length > 0) ? 'pointer' : 'not-allowed',
                            fontSize: '14px',
                            opacity: (trashLinks.length > 0 || trashFolders.length > 0) ? 1 : 0.3,
                            transition: 'opacity 0.2s'
                        }}
                    >
                        Buidar paperera
                    </button>
                </div>
            </div>

            <div style={{ minHeight: '500px' }}>
                {(() => {
                    const combinedItems = getCombinedItems();

                    if (combinedItems.length === 0) {
                        return (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px',
                                color: '#6c757d',
                                fontSize: '16px'
                            }}>
                                La paperera està buida
                            </div>
                        );
                    }

                    return combinedItems.map((item) =>
                        item.type === 'folder'
                            ? renderTrashFolderCard(item)
                            : renderTrashLinkCard(item)
                    );
                })()}
            </div>

            <div style={{ paddingBottom: '50px' }} />
        </div>
    );
}

export default TrashView;
